/**
 * CostModeledBanner Component
 * Shows a banner when modeled Cost score is enabled, allowing users to toggle it off
 * Design matches PIModeledBanner and IAModeledBanner for consistency
 */

import { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface CostModeledBannerProps {
  useModeled: boolean;
  modeledPercent: number;
  calculatedPercent: number;
  costWeight: number;
  onToggle: (useModeled: boolean) => void;
}

// Helper to safely convert to number
function toSafeNumber(value: number | undefined | null): number {
  if (value === undefined || value === null) return 0;
  if (typeof value !== 'number') return 0;
  if (Number.isNaN(value)) return 0;
  return value;
}

export function CostModeledBanner({
  useModeled,
  modeledPercent,
  calculatedPercent,
  costWeight,
  onToggle,
}: CostModeledBannerProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const safeModeledPercent = toSafeNumber(modeledPercent);
  const safeCalculatedPercent = toSafeNumber(calculatedPercent);

  const handleToggleClick = () => {
    if (useModeled) {
      // Switching from modeled to calculator - show confirmation
      setShowConfirmDialog(true);
    } else {
      // Switching from calculator to modeled - direct toggle
      onToggle(true);
    }
  };

  const handleConfirmSwitch = () => {
    onToggle(false);
    setShowConfirmDialog(false);
  };

  if (!useModeled) {
    return (
      <Alert className="border-[#4a6a8f]/30 bg-[#5a7a9f]/5">
        <AlertCircle className="h-4 w-4 text-[#5a7a9f]" />
        <AlertTitle className="text-[#4a6a8f]">Cost Calculator Active</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <div className="text-[#5a7a9f]">
            <p>
              Your Cost score is currently being calculated from uploaded cost feedback data.
              Current score: <span className="font-semibold">{safeCalculatedPercent.toFixed(1)}%</span>
            </p>
            <p className="text-sm mt-1">
              To use a modeled value instead, switch using the toggle.
            </p>
          </div>
          <div className="flex items-center gap-4 ml-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#5a7a9f]">Modeled</span>
              <Switch
                checked={!useModeled}
                onCheckedChange={() => handleToggleClick()}
              />
              <span className="text-sm text-[#5a7a9f]">Calculator</span>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <Alert className="border-amber-500/30 bg-amber-500/5">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-700">Modeled Cost Score Active</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <div className="text-amber-600">
            <p>
              Your Cost score is currently set to a modeled value of{' '}
              <span className="font-semibold">{safeModeledPercent.toFixed(1)}%</span> (configured in Settings).
            </p>
            <p className="text-sm mt-1">
              To use actual cost data, switch to the Cost Calculator below.
            </p>
          </div>
          <div className="flex items-center gap-4 ml-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-amber-600">Modeled</span>
              <Switch
                checked={!useModeled}
                onCheckedChange={() => handleToggleClick()}
              />
              <span className="text-sm text-amber-600">Calculator</span>
            </div>
          </div>
        </AlertDescription>
      </Alert>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch to Cost Calculator?</DialogTitle>
            <DialogDescription>
              You are about to switch from using a modeled Cost score to the Cost Calculator.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This will:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
              <li>Replace your modeled Cost score ({safeModeledPercent.toFixed(1)}%) with calculated values</li>
              <li>Use your uploaded cost feedback data to calculate the score</li>
              <li>Update your MIPS score based on the calculated Cost score</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4">
              You can switch back to modeled scoring at any time from this page or the Configuration page.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSwitch}>
              Switch to Calculator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
